import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Paper, Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  formatIndianAuto,
  formatRupeesIndianCompact,
  premiumTooltip,
  horizontalGrid,
  indianValueAxis,
  indianCategoryAxis,
  STATUS_COLORS,
  defaultAnimation,
} from './echartsConfig';
import { reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';
import { getCurrency as currencySelector } from 'utils/redux/reducer/authentication-slice';

const TOP_N = 10;

const norm = s => String(s || '').toLowerCase().trim();

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Find dataset index by fuzzy label match (Confirmed / Tentative / Waitlisted). */
function datasetIndices(datasets) {
  const idx = needle =>
    datasets.findIndex(d => {
      const lab = norm(d.label);
      return lab.includes(needle);
    });
  return {
    iConf: idx('confirm'),
    iTent: idx('tent'),
    iWait: idx('wait'),
  };
}

function rowTotal(datasets, idxMap, i) {
  const { iConf, iTent, iWait } = idxMap;
  let t = 0;
  if (iConf >= 0) t += toNum(datasets[iConf].data[i]);
  if (iTent >= 0) t += toNum(datasets[iTent].data[i]);
  if (iWait >= 0) t += toNum(datasets[iWait].data[i]);
  return t;
}

/**
 * Drops legacy "Others" rows, sorts by pipeline total (desc), keeps top {@link TOP_N}.
 * Chart labels stay in that order (largest first) with default Y scale (reverse: false) so #1 is at the top.
 * KPIs use grand total across all companies in range; chart shows top N only.
 */
function prepareCompanyBobDisplay(data) {
  const rawLabels = (data?.labels || []).map(l => String(l ?? ''));
  const rawDatasets = (data?.datasets || []).map(ds => ({
    ...ds,
    data: (ds.data || []).map(toNum),
  }));

  const idxMap = datasetIndices(rawDatasets);
  const n = rawLabels.length;

  if (n === 0 || (idxMap.iConf < 0 && idxMap.iTent < 0 && idxMap.iWait < 0)) {
    return {
      labels: [],
      datasets: [],
      rowTotals: [],
      grandTotal: 0,
    };
  }

  const rows = [];
  for (let i = 0; i < n; i++) {
    const label = String(rawLabels[i] ?? '').trim();
    if (!label || norm(label) === 'others') continue;
    rows.push({
      label,
      conf: idxMap.iConf >= 0 ? toNum(rawDatasets[idxMap.iConf].data[i]) : 0,
      tent: idxMap.iTent >= 0 ? toNum(rawDatasets[idxMap.iTent].data[i]) : 0,
      wait: idxMap.iWait >= 0 ? toNum(rawDatasets[idxMap.iWait].data[i]) : 0,
      total: rowTotal(rawDatasets, idxMap, i),
    });
  }

  const rowsWithPipeline = rows.filter(r => r.total > 0);
  rowsWithPipeline.sort((a, b) => b.total - a.total);
  const grandTotal = rowsWithPipeline.reduce((s, r) => s + r.total, 0);

  const top = rowsWithPipeline.slice(0, TOP_N);
  const labels = top.map(r => r.label);

  const datasets = rawDatasets.map((ds, di) => {
    const newData = top.map(r => {
      if (di === idxMap.iConf) return r.conf;
      if (di === idxMap.iTent) return r.tent;
      if (di === idxMap.iWait) return r.wait;
      return 0;
    });
    return { ...ds, data: newData };
  });

  const idxMapFinal = datasetIndices(datasets);
  let rowTotals = labels.map((_, i) => rowTotal(datasets, idxMapFinal, i));

  const order = labels.map((_, i) => i).sort((a, b) => rowTotals[b] - rowTotals[a]);
  const labelsOrdered = order.map(i => labels[i]);
  const datasetsOrdered = datasets.map(ds => ({
    ...ds,
    data: order.map(i => toNum(ds.data[i])),
  }));
  rowTotals = order.map(i => rowTotals[i]);

  return {
    labels: labelsOrdered,
    datasets: datasetsOrdered,
    rowTotals,
    grandTotal,
  };
}

function computeCompanyBobKpis(labels, rowTotals, grandTotal) {
  if (!labels.length) {
    return {
      totalCorporateRevenue: 0,
      topCompany: '—',
      largestContributionPct: '—',
      corporateAccountsDisplay: '—',
    };
  }

  const topRev = rowTotals[0] || 0;
  const largestPct =
    grandTotal > 0 && topRev > 0 ? `${Math.round((topRev / grandTotal) * 1000) / 10}%` : '—';

  return {
    totalCorporateRevenue: grandTotal,
    topCompany: String(labels[0] ?? '—'),
    largestContributionPct: largestPct,
    corporateAccountsDisplay: String(labels.length),
  };
}

function rankPrefix(i) {
  return `#${i + 1}  `;
}

function buildEChartsOption(prepared) {
  const { labels, datasets } = prepared;
  if (!labels.length) return {};

  const idxMap = datasetIndices(datasets);

  const confData = idxMap.iConf >= 0 ? datasets[idxMap.iConf].data : labels.map(() => 0);
  const tentData = idxMap.iTent >= 0 ? datasets[idxMap.iTent].data : labels.map(() => 0);
  const waitData = idxMap.iWait >= 0 ? datasets[idxMap.iWait].data : labels.map(() => 0);

  const totals = labels.map((_, i) => toNum(confData[i]) + toNum(tentData[i]) + toNum(waitData[i]));

  const categoryLabels = [...labels].reverse();
  const confReversed = [...confData].reverse();
  const tentReversed = [...tentData].reverse();
  const waitReversed = [...waitData].reverse();
  const totalsReversed = [...totals].reverse();

  const rankedLabels = categoryLabels.map((l, i) => {
    const rank = labels.length - i;
    return `${rankPrefix(rank - 1)}${l}`;
  });

  const barRadius = [0, 4, 4, 0];

  return {
    ...defaultAnimation,
    grid: { ...horizontalGrid, right: 60 },
    tooltip: {
      ...premiumTooltip,
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(148,163,184,0.08)' } },
      formatter(params) {
        if (!Array.isArray(params) || !params.length) return '';
        const label = params[0].axisValueLabel || '';
        const companyName = label.replace(/^#\d+\s+/, '');
        let total = 0;
        params.forEach(p => { total += toNum(p.value); });

        const title = `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:6px">${companyName}</div>`;
        const totalRow = `<div style="font-weight:600;font-size:12px;color:#94a3b8;margin-bottom:4px">Total: ${getCurrencySymbol()}${formatIndianAuto(total)}</div>`;
        const rows = params
          .filter(p => p.value != null && p.value !== 0)
          .map(p => {
            const marker = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px"></span>`;
            return `<div style="display:flex;justify-content:space-between;align-items:center;gap:12px">${marker}<span style="flex:1">${p.seriesName}</span><span style="font-weight:600;font-variant-numeric:tabular-nums">${getCurrencySymbol()}${formatIndianAuto(p.value)}</span></div>`;
          })
          .join('');
        return title + totalRow + rows;
      },
    },
    xAxis: {
      ...indianValueAxis,
      position: 'bottom',
    },
    yAxis: {
      ...indianCategoryAxis,
      data: rankedLabels,
      axisLabel: {
        ...indianCategoryAxis.axisLabel,
        width: 140,
        overflow: 'truncate',
        formatter: (value) => value,
      },
    },
    series: [
      {
        name: 'Confirmed',
        type: 'bar',
        stack: 'pipeline',
        data: confReversed,
        barWidth: '55%',
        itemStyle: {
          color: STATUS_COLORS.confirmed,
          borderRadius: [0, 0, 0, 0],
        },
        emphasis: { itemStyle: { opacity: 0.85 } },
      },
      {
        name: 'Tentative',
        type: 'bar',
        stack: 'pipeline',
        data: tentReversed,
        barWidth: '55%',
        itemStyle: {
          color: STATUS_COLORS.tentative,
          borderRadius: [0, 0, 0, 0],
        },
        emphasis: { itemStyle: { opacity: 0.85 } },
      },
      {
        name: 'Waitlisted',
        type: 'bar',
        stack: 'pipeline',
        data: waitReversed,
        barWidth: '55%',
        itemStyle: {
          color: STATUS_COLORS.waitlisted,
          borderRadius: barRadius,
        },
        emphasis: { itemStyle: { opacity: 0.85 } },
        label: {
          show: true,
          position: 'right',
          fontSize: 10,
          color: '#6B7280',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          formatter: (params) => {
            const total = totalsReversed[params.dataIndex];
            return total > 0 ? formatRupeesIndianCompact(total) : '';
          },
        },
      },
    ],
  };
}


const CompanyWiseBobChart = ({ data, name, handleExport, handleEnlarge, showButton = true, embeddedInDialog = false }) => {
  const displayName = data?.name || name;
  const currencyContext = useSelector(currencySelector);
  const currencyKey = `${currencyContext?.currencySymbol || ''}|${currencyContext?.currency || ''}|${currencyContext?.currencyLocale || ''}`;

  const prepared = useMemo(() => prepareCompanyBobDisplay(data), [data]);

  const kpis = useMemo(
    () =>
      computeCompanyBobKpis(
        prepared.labels,
        prepared.rowTotals,
        prepared.grandTotal,
      ),
    [prepared],
  );

  const plotHeight = useMemo(() => {
    const n = prepared.labels.length || 1;
    if (embeddedInDialog) {
      return Math.min(720, Math.max(400, n * 40));
    }
    return Math.min(440, Math.max(240, n * 32));
  }, [prepared.labels.length, embeddedInDialog]);

  const option = useMemo(() => buildEChartsOption(prepared), [prepared]);

  const apiKpis = kpiMapToItems(data?.kpiMap);
  const infoTooltip = data?.infoText || `Top ${TOP_N} companies by total pipeline. Rank #1 at top.`;

  const paperSx = embeddedInDialog
    ? {
        ...reportChartPaperSx,
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'none',
        border: 'none',
        px: 0,
        py: 0,
      }
    : reportChartPaperSx;

  return (
    <Paper sx={paperSx}>
      {!embeddedInDialog && (
        <ReportCardHeader
          title={displayName}
          insight={infoTooltip}
          handleExport={handleExport}
          handleEnlarge={handleEnlarge}
          showButton={showButton}
        />
      )}

      <ReportKpiStrip
        items={apiKpis || [
          { label: 'Total Corporate Revenue', value: formatRupeesIndianCompact(kpis.totalCorporateRevenue) },
          { label: 'Top Company', value: kpis.topCompany },
          { label: 'Largest Contribution', value: kpis.largestContributionPct },
        ]}
      />

      <ReportInsightBanner text={data?.insightText} style={data?.insightStyle} />

      <Box
        sx={
          embeddedInDialog
            ? { width: '100%', flex: '1 1 auto', minHeight: plotHeight, minWidth: 0 }
            : { width: '100%', height: plotHeight }
        }>
        {prepared.labels.length > 0 ? (
          <ReactECharts
            key={currencyKey}
            option={option}
            style={{ width: '100%', height: '100%' }}
            notMerge
            lazyUpdate
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: embeddedInDialog ? 280 : 200,
              px: 2,
              py: 2,
            }}>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 360, lineHeight: 1.55 }}>
              No company-wise pipeline in this range (all values are zero, or no bookings have a company name).
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default CompanyWiseBobChart;
